import { apiClient } from '../../lib/api-client';
import { PATIENT_UHID_SEARCH_LIMIT } from '../../lib/patient-uhid-search';
import type {
  CreateIpdBedTransferPayload,
  CreateIpdBillPayload,
  CreateIpdDaycareAdmissionPayload,
  IpdBedTransfer,
  IpdBill,
  IpdBillPaymentInput,
  UpdateIpdBillReceiptPayload,
  IpdDaycareAdmission,
  IpdDaycareListFilters,
  IpdDaycareLookupOption,
  IpdDischargeSummary,
  IpdPatientCreditOption,
  IpdPrescription,
  Pagination,
  UpdateIpdBillPayload,
  UpdateIpdDaycareAdmissionPayload,
  UpsertIpdDischargeSummaryPayload,
  UpsertIpdPrescriptionPayload,
} from './types';

interface ApiResponse<T> { success: true; data: T }
interface PaginatedResponse<T> extends ApiResponse<T[]> { pagination: Pagination }

const compactParams = (params: object) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null),
);

export const fetchIpdDaycareAdmissions = async (filters: IpdDaycareListFilters) => {
  const response = await apiClient.get<PaginatedResponse<IpdDaycareAdmission>>('/ipd-daycare/admissions', { params: compactParams(filters) });
  return response.data;
};

export const fetchIpdDaycareAdmission = async (id: string) => {
  const response = await apiClient.get<ApiResponse<IpdDaycareAdmission>>(`/ipd-daycare/admissions/${id}`);
  return response.data.data;
};

export const createIpdDaycareAdmission = async (payload: CreateIpdDaycareAdmissionPayload) => {
  const response = await apiClient.post<ApiResponse<IpdDaycareAdmission>>('/ipd-daycare/admissions', payload);
  return response.data.data;
};

export const updateIpdDaycareAdmission = async (id: string, payload: UpdateIpdDaycareAdmissionPayload) => {
  const response = await apiClient.patch<ApiResponse<IpdDaycareAdmission>>(`/ipd-daycare/admissions/${id}`, payload);
  return response.data.data;
};

export const cancelIpdDaycareAdmission = async (id: string) => {
  const response = await apiClient.post<ApiResponse<IpdDaycareAdmission>>(`/ipd-daycare/admissions/${id}/cancel`);
  return response.data.data;
};

export const fetchIpdBedTransfers = async (admissionId: string) => {
  const response = await apiClient.get<ApiResponse<IpdBedTransfer[]>>(`/ipd-daycare/admissions/${admissionId}/bed-transfers`);
  return response.data.data;
};

export const createIpdBedTransfer = async (admissionId: string, payload: CreateIpdBedTransferPayload) => {
  const response = await apiClient.post<ApiResponse<IpdBedTransfer>>(`/ipd-daycare/admissions/${admissionId}/bed-transfers`, payload);
  return response.data.data;
};

export const fetchIpdPrescription = async (admissionId: string) => {
  const response = await apiClient.get<ApiResponse<IpdPrescription>>(`/ipd-daycare/admissions/${admissionId}/prescription`);
  return response.data.data;
};

export const saveIpdPrescription = async (admissionId: string, payload: UpsertIpdPrescriptionPayload) => {
  const response = await apiClient.put<ApiResponse<IpdPrescription>>(`/ipd-daycare/admissions/${admissionId}/prescription`, payload);
  return response.data.data;
};

export const fetchIpdDischargeSummary = async (admissionId: string) => {
  const response = await apiClient.get<ApiResponse<IpdDischargeSummary>>(`/ipd-daycare/admissions/${admissionId}/discharge-summary`);
  return response.data.data;
};

export const saveIpdDischargeSummary = async (admissionId: string, payload: UpsertIpdDischargeSummaryPayload) => {
  const response = await apiClient.put<ApiResponse<IpdDischargeSummary>>(`/ipd-daycare/admissions/${admissionId}/discharge-summary`, payload);
  return response.data.data;
};

export const finalizeIpdDischarge = async (admissionId: string) => {
  const response = await apiClient.post<ApiResponse<IpdDischargeSummary>>(`/ipd-daycare/admissions/${admissionId}/discharge-summary/finalize`);
  return response.data.data;
};

export const fetchIpdDaycareLookups = async (type: string, search = '', extra: { state?: string; ward?: string; requisitionSection?: string } = {}) => {
  const response = await apiClient.get<ApiResponse<IpdDaycareLookupOption[]>>('/ipd-daycare/lookups', { params: compactParams({ type, search, ...extra, limit: type.startsWith('patients') ? PATIENT_UHID_SEARCH_LIMIT : 500 }) });
  return response.data.data;
};

export const fetchIpdBills = async (admissionId: string, sortBy?: string | null, sortOrder?: 'asc' | 'desc') => {
  const response = await apiClient.get<ApiResponse<IpdBill[]>>(`/ipd-daycare/admissions/${admissionId}/bills`, {
    params: compactParams(sortBy ? { sortBy, sortOrder } : {}),
  });
  return response.data.data;
};

export const fetchIpdBill = async (billId: string) => {
  const response = await apiClient.get<ApiResponse<IpdBill>>(`/ipd-daycare/bills/${billId}`);
  return response.data.data;
};

export const createIpdBill = async (admissionId: string, payload: CreateIpdBillPayload) => {
  const response = await apiClient.post<ApiResponse<IpdBill>>(`/ipd-daycare/admissions/${admissionId}/bills`, payload);
  return response.data.data;
};

export const updateIpdBill = async (billId: string, payload: UpdateIpdBillPayload) => {
  const response = await apiClient.patch<ApiResponse<IpdBill>>(`/ipd-daycare/bills/${billId}`, payload);
  return response.data.data;
};

export const cancelIpdBill = async (billId: string) => {
  const response = await apiClient.post<ApiResponse<IpdBill>>(`/ipd-daycare/bills/${billId}/cancel`);
  return response.data.data;
};

export const addIpdBillReceipt = async (billId: string, payload: IpdBillPaymentInput) => {
  const response = await apiClient.post<ApiResponse<IpdBill>>(`/ipd-daycare/bills/${billId}/receipts`, payload);
  return response.data.data;
};

export const updateIpdBillReceipt = async (billId: string, paymentId: string, payload: UpdateIpdBillReceiptPayload) => {
  const response = await apiClient.patch<ApiResponse<IpdBill>>(`/ipd-daycare/bills/${billId}/receipts/${paymentId}`, payload);
  return response.data.data;
};

export const addIpdBillRefund = async (billId: string, amount: number, mode?: IpdBillPaymentInput['mode'], bankName?: string | null) => {
  const response = await apiClient.post<ApiResponse<IpdBill>>(`/ipd-daycare/bills/${billId}/refunds`, { amount, ...(mode ? { mode, bankName: bankName ?? null } : {}) });
  return response.data.data;
};

export const fetchIpdPatientCreditBills = async (patientId: string) => {
  const response = await apiClient.get<ApiResponse<IpdPatientCreditOption[]>>(`/ipd-daycare/patients/${patientId}/credit-bills`);
  return response.data.data;
};
